import React, { useEffect, useState, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import AddGroupModal from './AddGroupModal';
import { useAuth } from '../context/AuthContext';

import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { ArrowUpDown, ChevronDown, MoreHorizontal, EyeIcon } from "lucide-react"
 
import { Button } from "../components/ui/button"
import { Checkbox } from "../components/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu"
import { Input } from "../components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { AlertDialogDelete } from './AlertDialogDelete';
import { EditGroupDialog } from './EditGroupDialog';
import SheetGroupDetails from './SheetGroupDetails';

export interface DocumentGroup {
  id: string;
  name: string;
  description: string;
  createdAt: Date;
  fileCount?: number;
}

const DocumentGroups: React.FC = () => {
  const [groups, setGroups] = useState<DocumentGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [userRole, setUserRole] = useState<string>('');

  const columns: ColumnDef<DocumentGroup>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("name")}</div>
      ),
    },
    {
      accessorKey: "description",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Description
            <ArrowUpDown />
          </Button>
        )
      },
      cell: ({ row }) => <div className="lowercase">{row.getValue("description")}</div>,
    },
    {
      accessorKey: "createdAt",
      header: () => <div className="text-right">Created At</div>,
      cell: ({ row }) => {
        return <div className="text-right font-medium">{format(row.getValue("createdAt"), 'MMM dd, yyyy')}</div>
      },
    },
    // {
    //   accessorKey: "fileCount",
    //   header: () => <div className="text-right">Files</div>,
    //   cell: ({ row }) => {
    //     const fileCount: number = row.getValue("fileCount") || 0;
    //     return <div className="text-right font-medium">
    //       {fileCount} files
    //     </div>
    //   },
    // },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        const group = row.original
   
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              {
                userRole === 'admin' && (
                    <EditGroupDialog onGroupUpdated={handleFetchGroups} buttonTitle='Edit group' documentId={group.id} documentGroup={group} />
              )}
              {
                userRole === 'admin' && (
                    <AlertDialogDelete onDocumentDeleted={handleFetchGroups} collectionName='documentGroups' buttonTitle='Delete group' documentId={group.id} />
              )}
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Group</DropdownMenuLabel>
              <SheetGroupDetails group={group} buttonTitle='View details' onSheetDismiss={handleFetchGroups} />
              <DropdownMenuItem onClick={() => handleViewGroup(group.id)}>
              <EyeIcon className="h-5 w-5" />
                View files
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ];

  const fetchUserRole = useCallback(async () => {
    if (currentUser) {
      const userDoc = await getDocs(query(collection(firestore, 'users'), where('email', '==', currentUser.email)));
      if (!userDoc.empty) {
        setUserRole(userDoc.docs[0].data().role);
      }
    }
  }, [currentUser]);

  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  )
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = React.useState({})

  const table = useReactTable({
    data: groups,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  useEffect(() => {
    fetchUserRole();
    // fetchGroups();
  }, [fetchUserRole]);

  // const fetchGroups = async () => {
  //   try {
  //     const groupsRef = collection(firestore, 'documentGroups');
  //     const snapshot = await getDocs(groupsRef);
      
  //     // Get all groups
  //     const groupsData = snapshot.docs.map(doc => ({
  //       id: doc.id,
  //       ...doc.data(),
  //       createdAt: doc.data().createdAt.toDate()
  //     })) as DocumentGroup[];

  //     // Get file counts for each group
  //     const filesRef = collection(firestore, 'documents');
  //     const filesSnapshot = await getDocs(filesRef);
  //     const filesByGroup = filesSnapshot.docs.reduce((acc, doc) => {
  //       const groupId = doc.data().groupId;
  //       if (groupId) {
  //         acc[groupId] = (acc[groupId] || 0) + 1;
  //       }
  //       return acc;
  //     }, {} as Record<string, number>);

  //     // Combine group data with file counts
  //     const groupsWithCounts = groupsData.map(group => ({
  //       ...group,
  //       fileCount: filesByGroup[group.id] || 0
  //     }));

  //     setGroups(groupsWithCounts.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
  //   } catch (error) {
  //     console.error('Error fetching groups:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleViewGroup = (groupId: string) => {
    navigate(`/documents/${groupId}`);
  };

  // const handleEditGroup = async (groupId: string) => {
  //   if (editingGroup === groupId) {
  //     try {
  //       await updateDoc(doc(firestore, 'documentGroups', groupId), {
  //         name: editData.name,
  //         description: editData.description,
  //       });
  //       await fetchGroups();
  //       setEditingGroup(null);
  //     } catch (error) {
  //       console.error('Error updating group:', error);
  //     }
  //   } else {
  //     const group = groups.find(g => g.id === groupId);
  //     if (group) {
  //       setEditData({ name: group.name, description: group.description });
  //       setEditingGroup(groupId);
  //     }
  //   }
  // };

  const handleFetchGroups = async () => {
    // await fetchGroups();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <>


<div className="w-full">
      <div className="flex items-center py-4">
        <Input
          placeholder="Filter by month name"
          value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
          onChange={(event) =>
            table.getColumn("name")?.setFilterValue(event.target.value)
          }
          className="max-w-sm"
        />
        <AddGroupModal
        onGroupAdded={() => {}}
      />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="ml-auto">
              Columns <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                )
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          {table.getFilteredSelectedRowModel().rows.length} of{" "}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </div>

    </>
  );
};

export default DocumentGroups;
